import { atom, type Getter } from 'jotai/vanilla';

import { TeoCore } from '@/teoCore/TeoCore';
import type { TeoText } from '@/teoCore/text/TeoText';

import { useAtomValue } from '../jotai-svelte';
import {
  isUnset,
  REFRESH,
  unset,
  type NotSet,
  type Read,
  type ReadFunction,
  type SetStateActionWithRefresh,
  type TeoAtom,
} from './teoAtom.helpers';

export function teoAtom<TValue>(initialValue: Read<TValue>): TeoAtom<TValue> {
  const overwrittenAtom = atom<TValue | NotSet>(unset());

  const currentInitialValue = (get: Getter) => {
    return typeof initialValue === 'function' ?
        (initialValue as ReadFunction<TValue>)(get)
      : initialValue;
  };

  const readValue = (get: Getter) => {
    const value = get(overwrittenAtom);
    if (!isUnset(value)) {
      return value;
    }
    return currentInitialValue(get);
  };

  const anAtom = atom<TValue, [SetStateActionWithRefresh<TValue>], void>(
    readValue,
    (get, set, value) => {
      const newValue =
        typeof value === 'function' ? (value as (prev: TValue) => TValue)(readValue(get)) : value;
      if (newValue === REFRESH) {
        set(overwrittenAtom, unset());
      } else {
        set(overwrittenAtom, newValue);
      }
    },
  );

  return anAtom;
}

export function onMountWithCore<TSelfSetter extends (value: typeof REFRESH) => void>(
  callback: (setters: { setSelf: TSelfSetter; refresh: () => void }, core: TeoCore) => () => void,
) {
  // we only subscribe to coreChanges in development mode, as it should never change in prod.
  // if (import.meta.env?.MODE !== 'production') {
  return (setSelf: TSelfSetter) => {
    const setters = { setSelf, refresh: () => setSelf(REFRESH) };
    let unsubscribe: () => void = () => {};
    const coreSubscription = TeoCore.instance.events.addEventListener('coreChanged', (e) => {
      unsubscribe();
      unsubscribe = callback(setters, e);
    });
    if (TeoCore.instance) {
      unsubscribe = callback(setters, TeoCore.instance);
    }
    return () => {
      unsubscribe();
      coreSubscription();
    };
  };
  // } else {
  //   return (setSelf: TSelfSetter) => {
  //     const setters = { setSelf, refresh: () => setSelf(REFRESH) };
  //     return () => callback(setters, TeoCore.instance);
  //   };
  // }
}

export function onMountWithActiveText<TSelfSetter extends (value: typeof REFRESH) => void>(
  callback: (
    setters: { setSelf: TSelfSetter; refresh: () => void },
    activeText: TeoText,
    core: TeoCore,
  ) => () => void,
) {
  return (setSelf: TSelfSetter) => {
    const setters = { setSelf, refresh: () => setSelf(REFRESH) };
    let unsubscribe: () => void = () => {};
    const activeTextSubscription = TeoCore.instance.events.addEventListener(
      'activeTextChanged',
      (e) => {
        unsubscribe();
        unsubscribe = callback(setters, e, TeoCore.instance);
      },
    );
    if (TeoCore.instance.activeText) {
      unsubscribe = callback(setters, TeoCore.instance.activeText, TeoCore.instance);
    }
    return () => {
      unsubscribe();
      activeTextSubscription();
    };
  };
}

export function teoCoreAtom<TValue>(read: (core: TeoCore, get: Getter) => TValue): TeoAtom<TValue> {
  return teoAtom((get) => read(get(TeoCore$), get));
}

export function teoActiveTextAtom<TValue>(
  read: (activeText: TeoText, get: Getter) => TValue,
): TeoAtom<TValue> {
  return teoAtom((get) => read(get(ActiveText$), get));
}

export const TeoCore$ = teoAtom(() => TeoCore.instance);
// core should never change!
if (import.meta.env?.MODE !== 'production') {
  TeoCore$.onMount = (setSelf) => {
    return TeoCore.instance.events.addEventListener('coreChanged', () => {
      setSelf(REFRESH);
    });
  };
}

// This one is not reactive, but core should never change
export const useTeoCore = () => {
  return TeoCore.instance;
};

// This one is reactive, but we probably don't need it
// export const useTeoCoreState = () => useAtomValue(TeoCore$);

export const ActiveText$ = teoAtom((get) => get(TeoCore$).activeText);
ActiveText$.debugLabel = 'ActiveText$' + Math.random();
ActiveText$.onMount = (setSelf) => {
  return TeoCore.instance.events.addEventListeners({
    activeTextChanged: (e) => {
      setSelf(e);
    },
    coreChanged: () => setSelf(REFRESH),
  });
};
export const useActiveText = () => useAtomValue(ActiveText$);
